import { getInjector } from '../../utils/getInjector';
import { flatCartItemsWithAddon, getTrackItem } from './utils';
import slEventBus, { eventMetaMap } from '../../../../utils/slEventBus';

/**
 * @param {object} payload
 * @param {string} payload.itemId - The item request add to cart.
 * @param {object} payload.previousCartData - The cart data before item is removed
 * @param {object} payload.currentCartData - The cart data after item is removed
 * @param {object} [payload.trackerOptions] - The options for tracking (optional)
 * @param {object} payload.cartData - raw cart data after item is removed
 */

export const onTrackRemoveItem = async (payload) => {
  const { itemId, previousCartData, currentCartData, cartData } = payload;
  const trackOptions = {
    scope: 'shop',
    event_category: 'Cart',
    ...(payload.trackerOptions || {}),
  };
  const injector = await getInjector();
  const cartService = injector.get('cartService');
  const trackerService = injector.get('trackerService');
  const Analytics = injector.get('Analytics');
  const gaService = injector.get('gaService');
  const productService = injector.get('productService');
  const $filter = injector.get('$filter');
  const slFeatureService = injector.get('slFeatureService');
  const $rootScope = injector.get('$rootScope');

  const flattenPreviousCartItems = flatCartItemsWithAddon(
    previousCartData.items,
  );
  const flattenCurrentCartItems = flatCartItemsWithAddon(currentCartData.items);
  const removeItem = flattenPreviousCartItems.find(
    (item) => item.id === itemId,
  );

  const isDuplicatedItem =
    flattenPreviousCartItems.filter(
      (item) =>
        item.product_id === removeItem.product_id &&
        item.variation_id === removeItem.variation_id,
    ).length > 1;
  const trackItem = getTrackItem(
    removeItem.product_id,
    removeItem.variation_id,
    removeItem.type,
    flattenCurrentCartItems,
  );

  // third party tracking
  trackerService.track({
    type: trackerService.generalEventType.REMOVE_FROM_CART,
    data: {
      product: {
        ...removeItem.product,
        _id: removeItem.product_id,
      },
      quantity: removeItem.quantity,
      variationSelected: removeItem?.variation
        ? {
            ...removeItem.variation,
            _id: removeItem.variation_id,
          }
        : null,
      value: -1 * removeItem.quantity * (removeItem.price?.dollars || 0),
    },
  });

  if (slFeatureService.hasFeature('data_layer_info')) {
    // This event is for CST usage
    // see card SL-23292
    $rootScope.$emit(
      'remove.item.from.cart',
      cartService.getGaItemData(removeItem),
    );
  }

  // shoplytics
  if (isDuplicatedItem) {
    /**
     * BundlePricing Case:
     *  There are two items which have same productId and variationId, remove the item that out of bundlePricing,
     *  we need to add the action of update tracking for remaining bundlePricing item when normal item been deleted
     */
    cartService.tracking(
      trackerService.userActionTypes.UPDATE_ITEM,
      trackItem,
      trackOptions,
    );
  } else {
    cartService.tracking(
      trackerService.userActionTypes.REMOVE_ITEM,
      trackItem,
      trackOptions,
    );

    (removeItem?.addon_items || []).forEach(function (item) {
      cartService.tracking(
        trackerService.userActionTypes.REMOVE_ITEM,
        getTrackItem(
          item.product_id,
          item.variation_id,
          item.type,
          flattenPreviousCartItems,
        ),
        trackOptions,
      );
    });

    const products = flattenPreviousCartItems.filter((item) =>
      ['product', 'product_set', 'subscription_product'].includes(item.type),
    );
    const addonProducts = flattenPreviousCartItems.filter(
      (item) => item.type === 'addon_product',
    );
    // Remove all cart addon items (except from product) if last mainProductItem has been removed
    if (products.length === 1 && trackItem.type !== 'addon_product') {
      addonProducts.forEach((item) => {
        cartService.tracking(
          trackerService.userActionTypes.REMOVE_ITEM,
          getTrackItem(
            item.product_id,
            item.variation_id,
            item.type,
            flattenPreviousCartItems,
          ),
          trackOptions,
        );
      });
    }
  }

  // for storefront sdk
  slEventBus.emit(eventMetaMap.cart.itemRemoved, cartData);

  // Google Analysis
  if (Analytics.configuration.enhancedEcommerce && removeItem) {
    try {
      gaService.setUserId();

      Analytics.addProduct(
        productService.getSku(
          removeItem.product_id,
          removeItem.product.sku,
          removeItem.variation
            ? {
                ...removeItem.variation,
                key: removeItem.variation_id,
              }
            : null,
        ),
        $filter('translateModel')(removeItem.product.title_translations),
        '',
        '',
        productService.getVariationName(removeItem.variation),
        cartService.getItemPrice(removeItem)?.dollars?.toString(),
        removeItem.quantity.toString(),
        '',
        '0',
      );
      Analytics.trackCart('remove');
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }
};
